import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BarChart3, 
  Users, 
  Activity, 
  TrendingUp, 
  Settings, 
  Bell, 
  Search, 
  Plus,
  Home,
  BookOpen,
  Ticket,
  GraduationCap,
  User,
  Menu,
  X,
  AlertTriangle,
  Clock,
  CheckCircle,
  Package,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  SupportCard,
  TicketStatusBadge,
  DataTable
} from '../components/SupportBridge';
import ConversationalTicketing from '../components/ControlBridge/ConversationalTicketing';
import { RealTimeActivityFeed } from '../components/ControlBridge/Widgets/RealTimeActivityFeed';
import { SmartNotificationSystem } from '../components/ControlBridge/Widgets/SmartNotificationSystem';
import { useKeyboardShortcuts, createCommonShortcuts } from '../hooks/useKeyboardShortcuts';
import { exportTicketData, exportActivityData } from '../utils/exportUtils';
import PanelManager from '../components/PanelManager/PanelManager';
const ControlBridgeDemo: React.FC = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedBackend, setSelectedBackend] = useState('hivecore');
  const [showSearch, setShowSearch] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Keyboard shortcuts
  const shortcuts = createCommonShortcuts({
    openSearch: () => setShowSearch(true),
    openHelp: () => setShowHelp(true),
    refresh: () => window.location.reload(),
    toggleSidebar: () => setSidebarOpen(!sidebarOpen),
    newTicket: () => setActiveSection('support-bridge'),
    export: () => {
      if (activeSection === 'support-bridge') {
        exportTicketData(sampleTickets);
      } else {
        exportActivityData([]);
      }
    }
  });

  useKeyboardShortcuts(shortcuts);

  const navigationItems = [
    { id: 'dashboard', label: 'Control Center', icon: Home },
    { id: 'support-bridge', label: 'Support Bridge', icon: Ticket },
    { id: 'knowledgebase', label: 'Knowledge Base', icon: BookOpen },
    { id: 'environment', label: 'Environment', icon: Settings },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'automation', label: 'Automation', icon: Zap },
    { id: 'onboarding', label: 'Onboarding', icon: GraduationCap },
    { id: 'panel-manager', label: 'Panel Manager', icon: Package },
    { id: 'settings', label: 'Settings', icon: User }
  ];

  const backendOptions = [
    { id: 'hivecore', label: 'HiveCore', status: 'online' },
    { id: 'beehive-ai', label: 'Beehive AI', status: 'online' },
    { id: 'external', label: 'External SaaS', status: 'degraded' },
    { id: 'legacy', label: 'Legacy Systems', status: 'offline' }
  ];

  const sampleTickets = [
    {
      id: 'T-001',
      title: 'Login Issues with SSO',
      description: 'Users unable to authenticate via SSO',
      status: 'open' as const,
      priority: 'high' as const,
      assignee: 'John Doe',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-01-16')
    },
    {
      id: 'T-002', 
      title: 'Performance Degradation',
      description: 'Dashboard loading slowly',
      status: 'in-progress' as const,
      priority: 'medium' as const,
      assignee: 'Jane Smith',
      createdAt: new Date('2024-01-14'),
      updatedAt: new Date('2024-01-16')
    }
  ];


  const stats = [
    {
      title: 'Active Tickets',
      value: '42',
      change: '+8.3%',
      trend: 'up',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/20',
      iconColor: 'text-blue-300'
    },
    {
      title: 'Avg Response Time',
      value: '2.3h',
      change: '-12.5%',
      trend: 'down',
      icon: Activity,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/20',
      iconColor: 'text-green-300'
    },
    {
      title: 'Resolution Rate',
      value: '94.2%',
      change: '+2.1%',
      trend: 'up',
      icon: TrendingUp,
      color: 'from-slate-500 to-gray-500',
      bgColor: 'bg-slate-500/20',
      iconColor: 'text-slate-300'
    },
    {
      title: 'Customer Satisfaction',
      value: '4.8/5',
      change: '+0.3',
      trend: 'up',
      icon: BarChart3,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-500/20',
      iconColor: 'text-orange-300'
    },
    {
      title: 'Escalated Issues',
      value: '7',
      change: '-15.2%',
      trend: 'down',
      icon: AlertTriangle,
      color: 'from-red-500 to-pink-500',
      bgColor: 'bg-red-500/20',
      iconColor: 'text-red-300'
    },
    {
      title: 'Knowledge Articles',
      value: '156',
      change: '+12.4%',
      trend: 'up',
      icon: BookOpen,
      color: 'from-indigo-500 to-slate-500',
      bgColor: 'bg-indigo-500/20',
      iconColor: 'text-indigo-300'
    },
    {
      title: 'Team Workload',
      value: '78%',
      change: '+5.1%',
      trend: 'up',
      icon: Clock,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-500/20',
      iconColor: 'text-amber-300'
    },
    {
      title: 'First Contact Resolution',
      value: '89.3%',
      change: '+3.7%',
      trend: 'up',
      icon: CheckCircle,
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-500/20',
      iconColor: 'text-emerald-300'
    }
  ];

  const renderMainContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderControlCenter();
      case 'support-bridge':
        return renderSupportBridge();
      case 'knowledgebase':
        return renderKnowledgebase();
      case 'environment':
        return renderEnvironment();
      case 'analytics':
        return renderAnalytics();
      case 'automation':
        return renderAutomation();
      case 'onboarding':
        return renderOnboarding();
      case 'panel-manager':
        return renderPanelManager();
      case 'settings':
        return renderSettings();
      default:
        return renderControlCenter();
    }
  };

  const renderControlCenter = () => (
    <div className="space-y-6">
      {/* Control Bridge Header with Backend Selector */}
      <div className="control-bridge-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <motion.h2 
              className="text-3xl font-bold text-white"
              animate={{
                x: [0, 3, 0, -2, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
                repeatType: "loop"
              }}
            >
              Control Bridge
            </motion.h2>
            <p className="text-slate-400 mt-1">Universal support and automation platform</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-slate-800/50">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-slate-300">All Systems Online</span>
            </div>
            <select 
              value={selectedBackend}
              onChange={(e) => setSelectedBackend(e.target.value)}
              className="bg-slate-800/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#3439fa]"
            >
              {backendOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label} ({option.status})
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Search tickets, knowledge base, or ask AI assistant... (⌘K)"
                              className="violett-input w-full pl-10"
              onClick={() => setShowSearch(true)}
              readOnly
            />
          </div>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button className="bg-[#3439fa] hover:bg-[#2832e8] text-white border-0 px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl">
                          <Plus size={16} className="mr-2" />
              Create Ticket
            </Button>
            </motion.div>
        </div>
      </div>

      {/* Enhanced Analytics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: index * 0.1,
              type: "spring",
              stiffness: 120,
              damping: 15
            }}
            whileHover={{ 
              scale: 1.05, 
              y: -5,
              transition: { duration: 0.3, type: "spring", stiffness: 300 }
            }}
            whileTap={{ scale: 0.98 }}
            className="evolut-metric-card premium-card-hover cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-green-400 mt-1">{stat.change}</p>
              </div>
              <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg`}>
                <stat.icon size={24} className="text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* My Open Tickets */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">My Open Tickets</h2>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
            View All
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sampleTickets.map((ticket, index) => (
            <motion.div
              key={ticket.id}
              initial={{ opacity: 0, y: 30, rotateX: 15 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.12,
                type: "spring",
                stiffness: 100,
                damping: 20
              }}
              whileHover={{ 
                scale: 1.03, 
                y: -3,
                transition: { duration: 0.2, type: "spring", stiffness: 400 }
              }}
            >
              <SupportCard
                variant="ticket"
                title={ticket.title}
                description={ticket.description}
                priority={ticket.priority}
                status={ticket.status}
                className="h-full"
              >
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">ID:</span>
                    <span className="text-white font-mono">{ticket.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Assignee:</span>
                    <span className="text-white">{ticket.assignee}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Updated:</span>
                    <span className="text-white">{ticket.updatedAt.toLocaleDateString()}</span>
                  </div>
                </div>
              </SupportCard>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderKnowledgebase = () => (
    <div className="space-y-6">
      {/* Smart Search & Quick Access */}
      <div className="violett-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Knowledge Assistant</h2>
          <div className="flex items-center space-x-2 text-sm text-slate-400">
            <Activity size={16} />
            <span>AI-Powered</span>
          </div>
        </div>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search knowledge base or ask AI assistant..."
            className="violett-input w-full pl-10 pr-4"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {['Authentication', 'Password Reset', 'SSO Issues', 'Performance', 'API Errors'].map((tag) => (
            <motion.button
              key={tag}
              className="px-3 py-1 text-xs bg-blue-500/20 text-blue-300 rounded-full border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {tag}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Contextual Suggestions Based on Current Tickets */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Suggested for Your Tickets</h3>
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
            View All Suggestions
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: 'SSO Authentication Troubleshooting',
              summary: 'Common solutions for SSO login failures and configuration issues',
              category: 'Authentication',
              relevance: 'High - Related to T-001',
              rating: 4.8,
              readTime: '3 min',
              helpful: 89
            },
            {
              title: 'Dashboard Performance Optimization',
              summary: 'Steps to diagnose and resolve slow dashboard loading issues',
              category: 'Performance',
              relevance: 'Medium - Related to T-002',
              rating: 4.6,
              readTime: '5 min',
              helpful: 76
            }
          ].map((article, index) => (
            <motion.div
              key={article.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="violett-card hover:bg-slate-800/60 transition-colors cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-white group-hover:text-blue-300 transition-colors">
                    {article.title}
                  </h4>
                  <p className="text-sm text-slate-400 mt-1">{article.summary}</p>
                </div>
                <div className="ml-3 text-right">
                  <div className="flex items-center space-x-1 text-yellow-400">
                    <span className="text-sm">★</span>
                    <span className="text-sm">{article.rating}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-3">
                  <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded">
                    {article.category}
                  </span>
                  <span className="text-slate-500">{article.readTime}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">{article.relevance}</span>
                  <span className="text-slate-500">👍 {article.helpful}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Quick Categories for Analysts */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Quick Reference</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Troubleshooting', icon: '🔧', count: 45 },
            { name: 'How-to Guides', icon: '📖', count: 32 },
            { name: 'API Documentation', icon: '⚡', count: 28 },
            { name: 'Release Notes', icon: '📋', count: 12 }
          ].map((category) => (
            <motion.div
              key={category.name}
              className="violett-card text-center hover:bg-slate-800/60 transition-colors cursor-pointer group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="text-2xl mb-2">{category.icon}</div>
              <h4 className="font-medium text-white group-hover:text-blue-300 transition-colors">
                {category.name}
              </h4>
              <p className="text-sm text-slate-400">{category.count} articles</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent & Trending */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white">Recently Updated</h3>
          <div className="space-y-2">
            {[
              'API Rate Limiting Best Practices',
              'New SSO Provider Integration',
              'Dashboard Widget Configuration'
            ].map((title, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-800/40 transition-colors cursor-pointer"
              >
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-sm text-slate-300 hover:text-white transition-colors">
                  {title}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-white">Trending This Week</h3>
          <div className="space-y-2">
            {[
              'Password Reset Automation',
              'Performance Monitoring Setup',
              'User Permission Management'
            ].map((title, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-800/40 transition-colors cursor-pointer"
              >
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span className="text-sm text-slate-300 hover:text-white transition-colors">
                  {title}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderEnvironment = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Environment Management</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="control-bridge-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Environment Status</h3>
          <div className="space-y-3">
            {backendOptions.map(backend => (
              <div key={backend.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    backend.status === 'online' ? 'bg-green-400' : 
                    backend.status === 'degraded' ? 'bg-yellow-400' : 'bg-red-400'
                  }`}></div>
                  <span className="text-white font-medium">{backend.label}</span>
                </div>
                <span className={`text-sm px-2 py-1 rounded ${
                  backend.status === 'online' ? 'bg-green-500/20 text-green-300' :
                  backend.status === 'degraded' ? 'bg-yellow-500/20 text-yellow-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {backend.status}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="control-bridge-card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Button className="w-full bg-[#3439fa] hover:bg-[#2832e8] text-white">
              Run Environment Validation
            </Button>
            <Button className="w-full bg-slate-700 hover:bg-slate-600 text-white">
              Rotate Secrets
            </Button>
            <Button className="w-full bg-slate-700 hover:bg-slate-600 text-white">
              View Audit Logs
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Analytics and Insights</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="control-bridge-card p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                <p className={`text-sm mt-1 ${stat.trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                  {stat.change}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon size={24} className={stat.iconColor} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Automation and Workflows</h2>
      <div className="control-bridge-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Active Workflows</h3>
        <p className="text-slate-400">Workflow orchestration and automation management coming soon...</p>
      </div>
    </div>
  );

  const renderOnboarding = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Onboarding and Training</h2>
      <div className="control-bridge-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Cohort-Based Learning</h3>
        <p className="text-slate-400">Interactive onboarding flows and training modules coming soon...</p>
      </div>
    </div>
  );

  const renderSupportBridge = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-white">Support Bridge</h2>
        <div className="flex gap-3">
          <TicketStatusBadge status="open" />
          <TicketStatusBadge status="in-progress" />
          <TicketStatusBadge status="resolved" />
          <TicketStatusBadge status="closed" />
        </div>
      </div>

      {/* Three-column layout: Conversational Ticketing + Dashboard + Activity */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Conversational Ticketing Panel */}
        <div className="xl:col-span-1">
          <ConversationalTicketing
            onTicketCreated={(ticket) => {
              console.log('New ticket created:', ticket);
            }}
            onMessageSent={(message) => {
              console.log('Message sent:', message);
            }}
            className="h-[700px]"
          />
        </div>

        {/* Support Bridge Dashboard */}
        <div className="xl:col-span-1 space-y-6">
          {/* Smart Notifications */}
          <SmartNotificationSystem
            userId="support-analyst"
            maxItems={6}
            enableGrouping={true}
            enableFiltering={true}
            autoRefresh={true}
          />
          {/* Escalation & Workflow Tools */}
      <div className="control-bridge-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Escalation and Workflow Tools</h3>
          <div className="flex items-center space-x-2 text-sm text-slate-400">
            <Activity size={16} />
            <span>SOMA → FixBee → HITL</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.button
            className="control-bridge-card p-4 bg-gradient-to-br from-red-500/20 to-pink-500/20 border border-red-500/30 hover:from-red-500/30 hover:to-pink-500/30 transition-all duration-200 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-red-500/20 group-hover:bg-red-500/30 transition-colors">
                <AlertTriangle size={20} className="text-red-300" />
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-white">Request Urgent Help</h4>
                <p className="text-sm text-slate-400">Escalate to senior analyst</p>
              </div>
            </div>
          </motion.button>

          <motion.button
            className="control-bridge-card p-4 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 hover:from-blue-500/30 hover:to-indigo-500/30 transition-all duration-200 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-blue-500/20 group-hover:bg-blue-500/30 transition-colors">
                <BookOpen size={20} className="text-blue-300" />
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-white">Auto-KCS Link</h4>
                <p className="text-sm text-slate-400">Find relevant articles</p>
              </div>
            </div>
          </motion.button>

          <motion.button
            className="control-bridge-card p-4 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 hover:from-green-500/30 hover:to-emerald-500/30 transition-all duration-200 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-green-500/20 group-hover:bg-green-500/30 transition-colors">
                <Clock size={20} className="text-green-300" />
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-white">View History</h4>
                <p className="text-sm text-slate-400">Ticket audit trail</p>
              </div>
            </div>
          </motion.button>
        </div>
      </div>

      {/* Tickets Table */}
      <DataTable
        className="control-bridge-card"
        columns={[
          { id: 'id', header: 'ID', accessor: 'id', sortable: true },
          { id: 'title', header: 'Title', accessor: 'title', sortable: true },
          { id: 'status', header: 'Status', accessor: 'status', sortable: true },
          { id: 'priority', header: 'Priority', accessor: 'priority', sortable: true },
          { id: 'assignee', header: 'Assignee', accessor: 'assignee', sortable: true }
        ]}
        data={sampleTickets}
        onRowClick={(row) => console.log('Row click:', row)}
      />

          {/* Workflow Status Indicator */}
          <div className="control-bridge-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Current Workflow Status</h3>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-400">Active</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-blue-300">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-white">SOMA Analysis</h4>
                    <p className="text-sm text-slate-400">Automated issue classification</p>
                  </div>
                </div>
                <CheckCircle size={20} className="text-green-400" />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-orange-300">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-white">FixBee Processing</h4>
                    <p className="text-sm text-slate-400">Automated resolution attempt</p>
                  </div>
                </div>
                <Clock size={20} className="text-orange-400 animate-spin" />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 opacity-50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-slate-500/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-slate-400">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-400">HITL Review</h4>
                    <p className="text-sm text-slate-500">Human-in-the-loop validation</p>
                  </div>
                </div>
                <div className="w-5 h-5 rounded-full border-2 border-slate-500"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Real-Time Activity Feed */}
        <div className="xl:col-span-1">
          <RealTimeActivityFeed
            maxItems={40}
            autoRefresh={true}
            refreshInterval={3000}
            showFilters={true}
            enablePause={true}
            height="700px"
          />
        </div>
      </div>
    </div>
  );


  const renderPanelManager = () => (
    <div className="space-y-6">
      <PanelManager />
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-white">Settings and Profile</h2>
      <div className="control-bridge-card p-6">
        <p className="text-slate-400">Profile management, notification preferences, and organization settings coming soon...</p>
      </div>
    </div>
  );

  return (
    <div className="h-screen w-screen glass-dark flex overflow-hidden border-2 border-slate-700/30">
      {/* Sidebar Navigation */}
      <motion.div 
        className={`relative h-full flex flex-col glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-r border-slate-700/50 shadow-2xl transition-all duration-300 overflow-hidden ${
          sidebarOpen ? 'w-[280px] p-4' : 'w-16 p-2'
        }`}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ 
          duration: 0.4,
          type: "spring",
          stiffness: 500,
          damping: 30
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center space-x-3">
                <img 
                  src="/assets/control-bridge-logo.png" 
                  alt="Control Bridge" 
                  className="w-8 h-8 object-contain"
                />
                <div>
                  <motion.h1 
                    className="text-xl font-bold text-gradient"
                    animate={{
                      x: [0, 2, 0, -1, 0],
                    }}
                    transition={{
                      duration: 8,
                      repeat: Infinity,
                      ease: "easeInOut",
                      repeatType: "loop"
                    }}
                  >
                    Control Bridge
                  </motion.h1>
                  <p className="text-xs text-slate-400">Universal Platform</p>
                </div>
              </div>
            </motion.div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hover:bg-slate-800/50 text-white p-2"
          >
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </Button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 space-y-2">
          {navigationItems.map((item, index) => (
            <motion.button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`w-full flex items-center space-x-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                activeSection === item.id
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
              }`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <item.icon size={20} />
              {sidebarOpen && (
                <span className="font-medium">{item.label}</span>
              )}
            </motion.button>
          ))}
        </nav>

        {/* Footer */}
        {sidebarOpen && (
          <motion.div
            className="mt-6 pt-4 border-t border-slate-700/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center space-x-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <User size={16} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Support Analyst</p>
                <p className="text-xs text-slate-400">Online</p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-full">
        {/* Top Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass-dark bg-gradient-to-r from-slate-900/80 to-slate-800/60 backdrop-blur-xl border-b border-slate-700/50 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white capitalize">
                {navigationItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
              </h2>
              <p className="text-slate-400">
                {activeSection === 'dashboard' && 'Overview of your support activities and metrics'}
                {activeSection === 'tickets' && 'Manage and track your assigned tickets'}
                {activeSection === 'knowledgebase' && 'Browse and search knowledge articles'}
                {activeSection === 'community' && 'Connect with peers and share knowledge'}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="sm" className="hover:bg-slate-800/50 text-white">
                <Bell size={16} />
              </Button>
              <Button variant="ghost" size="sm" className="hover:bg-slate-800/50 text-white">
                <Settings size={16} />
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Content Area */}
        <div className="flex-1 p-6 overflow-auto h-full bg-slate-900/50 backdrop-blur-sm">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              {renderMainContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Enhanced Search Modal */}
      <AnimatePresence>
        {showSearch && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center pt-32"
            onClick={() => setShowSearch(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              className="control-bridge-glass-dark rounded-2xl w-full max-w-2xl mx-4 p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Search className="w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search everything..."
                    className="flex-1 bg-transparent text-white placeholder-slate-400 text-lg focus:outline-none"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSearch(false)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X size={16} />
                  </Button>
                </div>
                
                <div className="border-t border-slate-700/50 pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="text-slate-300 font-medium mb-2">Quick Actions</h4>
                      <div className="space-y-1">
                        <div className="text-slate-400 hover:text-white cursor-pointer">Create new ticket</div>
                        <div className="text-slate-400 hover:text-white cursor-pointer">View all tickets</div>
                        <div className="text-slate-400 hover:text-white cursor-pointer">Check system status</div>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-slate-300 font-medium mb-2">Recent</h4>
                      <div className="space-y-1">
                        <div className="text-slate-400 hover:text-white cursor-pointer">Ticket #CB-2025-001</div>
                        <div className="text-slate-400 hover:text-white cursor-pointer">ETL Job Report</div>
                        <div className="text-slate-400 hover:text-white cursor-pointer">Weekly Dashboard</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-700/50 pt-3 flex items-center justify-between text-xs text-slate-500">
                  <div className="flex gap-4">
                    <span>↑↓ Navigate</span>
                    <span>↵ Select</span>
                    <span>ESC Close</span>
                  </div>
                  <div>⌘K to open search</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="control-bridge-glass-dark rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Keyboard Shortcuts</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHelp(false)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X size={16} />
                  </Button>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Open search</span>
                    <span className="text-white font-mono">⌘ + K</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Toggle sidebar</span>
                    <span className="text-white font-mono">⌘ + B</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">New ticket</span>
                    <span className="text-white font-mono">⌘ + N</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Export data</span>
                    <span className="text-white font-mono">⌘ + Shift + E</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Refresh</span>
                    <span className="text-white font-mono">⌘ + R</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Show help</span>
                    <span className="text-white font-mono">Shift + ?</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ControlBridgeDemo;
